import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, resolveDashboardApiError } from '../services/dashboardApi';
import './DashboardPage.css';

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value) {
  return `PHP ${toNumber(value).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [access, setAccess] = useState({
    is_admin: false,
    role: 'user',
    branch_id: null,
    branch: null,
    scopes: [],
  });
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [collections, setCollections] = useState([]);
  const [agents, setAgents] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [branches, setBranches] = useState([]);

  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await dashboardApi.listSummary({ bustCache: true });
      const payload = response?.data || {};

      setAccess(payload.access || {
        is_admin: false,
        role: 'user',
        branch_id: null,
        branch: null,
        scopes: [],
      });
      setCollections(Array.isArray(payload.collections) ? payload.collections : []);
      setAgents(Array.isArray(payload.agents) ? payload.agents : []);
      setPaymentRequests(Array.isArray(payload.payment_requests) ? payload.payment_requests : []);
      setBranches(Array.isArray(payload.branches) ? payload.branches : []);
    } catch (err) {
      setError(resolveDashboardApiError(err));
      setCollections([]);
      setAgents([]);
      setPaymentRequests([]);
      setBranches([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const isAdmin = Boolean(access?.is_admin);

  const branchOptions = useMemo(() => {
    const names = new Set();
    branches.forEach((branch) => {
      const name = String(branch?.name || '').trim();
      if (name) {
        names.add(name);
      }
    });
    collections.forEach((collection) => {
      const name = String(collection?.branch || '').trim();
      if (name) {
        names.add(name);
      }
    });
    agents.forEach((agent) => {
      const name = String(agent?.branch || '').trim();
      if (name) {
        names.add(name);
      }
    });
    paymentRequests.forEach((request) => {
      const name = String(request?.branch || request?.branch_name || '').trim();
      if (name) {
        names.add(name);
      }
    });
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [branches, collections, agents, paymentRequests]);

  const activeBranch = useMemo(() => {
    if (isAdmin) {
      return selectedBranch;
    }

    const assignedBranch = String(access?.branch || '').trim();
    return assignedBranch || 'all';
  }, [isAdmin, selectedBranch, access]);

  const filteredCollections = useMemo(() => {
    if (activeBranch === 'all') {
      return collections;
    }

    return collections.filter((item) => String(item?.branch || '').trim() === activeBranch);
  }, [collections, activeBranch]);

  const filteredAgents = useMemo(() => {
    if (activeBranch === 'all') {
      return agents;
    }

    return agents.filter((item) => String(item?.branch || '').trim() === activeBranch);
  }, [agents, activeBranch]);

  const filteredRequests = useMemo(() => {
    if (activeBranch === 'all') {
      return paymentRequests;
    }

    return paymentRequests.filter((item) => String(item?.branch || item?.branch_name || '').trim() === activeBranch);
  }, [paymentRequests, activeBranch]);

  const totalCollections = useMemo(
    () => filteredCollections.reduce((sum, item) => sum + toNumber(item?.premium_amount), 0),
    [filteredCollections]
  );

  const activeAgents = useMemo(
    () => filteredAgents.filter((item) => normalizeText(item?.status) === 'active').length,
    [filteredAgents]
  );

  const pendingRequests = useMemo(
    () => filteredRequests.filter((item) => normalizeText(item?.status) === 'pending').length,
    [filteredRequests]
  );

  const recentCollections = useMemo(
    () => filteredCollections.slice(0, 5),
    [filteredCollections]
  );

  const recentPaymentRequests = useMemo(
    () => filteredRequests.slice(0, 5),
    [filteredRequests]
  );

  return (
    <div className="dashboardv2-page module-screen">
      <section className="dashboardv2-hero">
        <div className="dashboardv2-hero-copy">
          <p className="dashboardv2-eyebrow">FinDesk Dashboard</p>
          <h1>Welcome, {user?.full_name || user?.username || 'User'}</h1>
          <p className="dashboardv2-subtitle">
            {isAdmin ? 'All branch activity overview' : `Branch: ${access?.branch || 'Unassigned'}`}
          </p>
        </div>

        <div className="dashboardv2-hero-actions">
          {isAdmin ? (
            <label className="dashboardv2-field" htmlFor="dashboard-branch-filter">
              <span>Branch View</span>
              <select
                id="dashboard-branch-filter"
                value={selectedBranch}
                onChange={(event) => setSelectedBranch(event.target.value)}
              >
                <option value="all">All Branches</option>
                {branchOptions.map((branchName) => (
                  <option key={branchName} value={branchName}>
                    {branchName}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="dashboardv2-branch-pill">
              {access?.branch || 'Unassigned'}
            </div>
          )}
          <button
            type="button"
            className="dashboardv2-refresh-btn"
            onClick={loadSummary}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </section>

      <section className="dashboardv2-stats">
        <article className="dashboardv2-stat-card">
          <p>Total Collections</p>
          <h2>{formatCurrency(totalCollections)}</h2>
        </article>
        <article className="dashboardv2-stat-card">
          <p>Active Agents</p>
          <h2>{activeAgents.toLocaleString('en-PH')}</h2>
        </article>
        <article className="dashboardv2-stat-card">
          <p>Pending Requests</p>
          <h2>{pendingRequests.toLocaleString('en-PH')}</h2>
        </article>
        <article className="dashboardv2-stat-card">
          <p>Recent Collections</p>
          <h2>{recentCollections.length.toLocaleString('en-PH')}</h2>
        </article>
      </section>

      {error ? <div className="error-box">{error}</div> : null}

      {isLoading ? (
        <section className="dashboardv2-loading">
          Loading dashboard data...
        </section>
      ) : (
        <section className="dashboardv2-panels">
          <article className="dashboardv2-panel">
            <header className="dashboardv2-panel-header">
              <h3>Recent Collections</h3>
              <span>{recentCollections.length} record(s)</span>
            </header>
            <div className="dashboardv2-panel-body">
              {recentCollections.length === 0 ? (
                <p className="dashboardv2-empty">No collections recorded yet.</p>
              ) : (
                <div className="dashboardv2-list">
                  {recentCollections.map((item) => (
                    <div key={`collection-${item.id}`} className="dashboardv2-list-item">
                      <div className="dashboardv2-list-main">
                        <p className="dashboardv2-list-title">{item.client_name || 'Unnamed client'}</p>
                        <p className="dashboardv2-list-meta">
                          {item.product || 'No product'}
                          {' | '}
                          {item.agent_name || 'No agent'}
                          {isAdmin && item.branch ? ` | ${item.branch}` : ''}
                        </p>
                      </div>
                      <div className="dashboardv2-list-right">
                        <strong>{formatCurrency(item.premium_amount)}</strong>
                        <span className={`status-badge status-${normalizeText(item.status)}`}>
                          {item.status || 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </article>

          <article className="dashboardv2-panel">
            <header className="dashboardv2-panel-header">
              <h3>Recent Payment Requests</h3>
              <span>{recentPaymentRequests.length} record(s)</span>
            </header>
            <div className="dashboardv2-panel-body">
              {recentPaymentRequests.length === 0 ? (
                <p className="dashboardv2-empty">No payment requests yet.</p>
              ) : (
                <div className="dashboardv2-list">
                  {recentPaymentRequests.map((item) => (
                    <div key={`request-${item.id}`} className="dashboardv2-list-item">
                      <div className="dashboardv2-list-main">
                        <p className="dashboardv2-list-title">{item.requestor_name || 'Unknown requestor'}</p>
                        <p className="dashboardv2-list-meta">
                          {item.request_type || 'No type'}
                          {' | '}
                          {item.branch || item.branch_name || 'N/A'}
                        </p>
                      </div>
                      <div className="dashboardv2-list-right">
                        <strong>{formatCurrency(item.total_amount)}</strong>
                        <span className={`status-badge status-${normalizeText(item.status)}`}>
                          {item.status || 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </article>
        </section>
      )}
    </div>
  );
}
